import * as React from 'react';

import { ManagementFrameContext, useManagementFrameContext } from '@/components/ui/management-frame-context';
import { getDesktopVisibilityClasses, getMobileVisibilityClasses } from '@/components/ui/management-frame-variants';
import { MANAGEMENT_FRAME_BREAKPOINT } from './types/management-frame-variants-enums';
import { cn } from '@/lib/utils';

export interface ManagementFrameRootProps extends React.ComponentProps<'section'> {
  breakpoint?: MANAGEMENT_FRAME_BREAKPOINT;
}

function ManagementFrameRoot({
  breakpoint = MANAGEMENT_FRAME_BREAKPOINT.LG,
  className,
  ...props
}: ManagementFrameRootProps) {
  const contextValue = { breakpoint };

  return (
    <ManagementFrameContext value={contextValue}>
      <section
        data-slot="management-frame-root"
        {...props}
        className={cn(
          'overflow-hidden rounded-surface border border-border-default bg-surface-base shadow-none',
          className
        )}
        data-breakpoint={breakpoint}
      />
    </ManagementFrameContext>
  );
}

function ManagementFrameToolbar({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  useManagementFrameContext('ManagementFrame.Toolbar');

  return (
    <div
      data-slot="management-frame-toolbar"
      {...props}
      className={cn('border-b border-border-subtle p-3 sm:p-4', className)}
    />
  );
}

function ManagementFrameDesktop({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { breakpoint } = useManagementFrameContext('ManagementFrame.Desktop');

  return (
    <div
      data-slot="management-frame-desktop"
      {...props}
      className={cn(getDesktopVisibilityClasses(breakpoint), className)}
    />
  );
}

function ManagementFrameMobile({ className, ...props }: React.ComponentProps<'div'>) {
  const { breakpoint } = useManagementFrameContext('ManagementFrame.Mobile');

  return (
    <div
      data-slot="management-frame-mobile"
      {...props}
      className={cn(getMobileVisibilityClasses(breakpoint), className)}
    />
  );
}

function ManagementFrameState({ className, ...props }: React.ComponentProps<'div'>) {
  useManagementFrameContext('ManagementFrame.State');

  return (
    <div
      data-slot="management-frame-state"
      {...props}
      className={cn('min-h-32', className)}
    />
  );
}

function ManagementFramePagination({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  useManagementFrameContext('ManagementFrame.Pagination');

  return (
    <div
      data-slot="management-frame-pagination"
      {...props}
      className={cn(
        'flex min-h-12 items-center justify-end border-t border-border-subtle px-3 py-2 sm:px-4',
        className
      )}
    />
  );
}

export { ManagementFrameDesktop, ManagementFrameMobile, ManagementFramePagination, ManagementFrameRoot, ManagementFrameState, ManagementFrameToolbar };
